import { memo, useCallback, useMemo } from 'react';
import { Pressable, StyleSheet, Text, View } from 'react-native';
import { MainStackParams } from '../../common/screen_params';
import { commonStyles } from '../../common/styles';
import AppColors from '../../common/colors';

type Props = MainStackParams['MainDialogMobile'] & {
  submit: (action?: string) => void;
};

const MainDialogMobile = memo<Props>(
  ({ title, text, actions, defaultAction, submit }) => {
    const close = useCallback(
      () => submit(defaultAction),
      [defaultAction, submit],
    );

    const actionElements = useMemo(
      () =>
        actions.map((e, i) => (
          <MainDialogItem key={i} {...e} primary={i === 0} onPress={submit} />
        )),
      [actions, submit],
    );

    return (
      <View style={styles.container}>
        <Pressable onPress={close} style={commonStyles.fill} />
        <View style={styles.dialogContainer}>
          <Text style={styles.title}>{title}</Text>
          <Text style={styles.text}>{text}</Text>
          {actionElements}
        </View>
      </View>
    );
  },
);

type DialogAction = {
  text: string;
  primary?: boolean;
  action?: string;
  onPress?: (action?: string) => void;
};

const MainDialogItem = memo<DialogAction>(
  ({ text, primary, action, onPress }) => {
    const [containerStyle, textStyle] = useMemo(
      () =>
        primary
          ? [styles.actionContainer, styles.actionText]
          : [styles.actionContainerSecondary, styles.actionTextSecondary],
      [primary],
    );

    const press = useCallback(() => onPress?.(action), [action, onPress]);

    return (
      <Pressable onPress={press} style={containerStyle}>
        <Text style={textStyle}>{text}</Text>
      </Pressable>
    );
  },
);

const styles = StyleSheet.create({
  container: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  dialogContainer: {
    backgroundColor: AppColors.containerGray,
    marginHorizontal: 40,
    paddingTop: 24,
    paddingBottom: 12,
    paddingHorizontal: 24,
    borderRadius: 16,
  },
  title: {
    ...commonStyles.text,
    color: 'white',
    fontSize: 24,
    fontWeight: '700',
    marginBottom: 6,
    textAlign: 'center',
  },
  text: {
    ...commonStyles.text,
    color: 'white',
    fontSize: 20,
    marginBottom: 12,
    textAlign: 'center',
  },
  actionContainer: {
    backgroundColor: AppColors.mainBlue,
    alignItems: 'center',
    marginBottom: 12,
    paddingVertical: 16,
    borderRadius: 8,
  },
  actionContainerSecondary: {
    alignItems: 'center',
    marginBottom: 12,
    paddingVertical: 16,
  },
  actionText: {
    ...commonStyles.text,
    color: 'white',
    fontSize: 16,
  },
  actionTextSecondary: {
    ...commonStyles.text,
    color: '#6C7072',
    fontSize: 16,
  },
});

export default MainDialogMobile;
